import { feedbackAudioUrl, isCorrectFeedback } from '../feedbackAudio';
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import MiniTaskReplayButton from '../components/MiniTaskReplayButton';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import Grade1Letter from './Grade1Letter';
import SoundBasketArt from './SoundBasketArt';
import LetterSequence from './LetterSequence';
import useTourIdleHelp from './useTourIdleHelp';
import { setRoundAudioPlayback } from './pronunciationPlayback';
import './soundHunt.css';
import './soundChoice.css';
import './letterHunt.css';
import './l4Tour3.css';
import './syllableFinal.css';

export default function SoundChoiceRoundView({ round, classTitle, onExit, onNextRound, onComplete,
  sharedAudioRef, localProgress, idleHelpDisabled = false }) {
  const baskets = round.task_type === 'sound_baskets';
  const isL4G05 = round.source_level === 4 && round.game_id === 'G05' && baskets;
  const syllables = round.assessment_mode === 'syllable_choice';
  const pictureWord = round.assessment_mode === 'picture_word_choice';
  const silent = !!round.silent_reading;
  const pictures = !baskets && round.choices?.some((choice) => choice.image);
  const [phase, setPhase] = useState('waiting');
  const [index, setIndex] = useState(0);
  const [armed, setArmed] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [offset, setOffset] = useState(null);
  const [dropOffset, setDropOffset] = useState(null);
  const [error, setError] = useState('');
  const [wrongFeedback, setWrongFeedback] = useState(0);
  const [referenceSound, setReferenceSound] = useState(null);
  const ownAudio = useRef(null);
  const audioRef = sharedAudioRef || ownAudio;
  const handlers = useRef({});
  const drag = useRef(null);
  const suppressClick = useRef(false);
  const source = useRef(null);
  const optionElements = useRef({});
  const completion = useRef(onComplete);
  completion.current = onComplete;
  useTourIdleHelp(phase === 'ready' && !offset && !idleHelpDisabled, () => handlers.current.help());

  useEffect(() => {
    let alive = true, running = false, locked = true, cursor = 0, retry, cancelAudio, cancelImage, cancelDelay;
    let events = [], chosen = false, helpShown = false;
    const report = () => localProgress(round.game_id, events);
    const playOnce = (id) => new Promise((resolve, reject) => {
      const url = feedbackAudioUrl(id, round.audio[id]);
      if (!alive || !url) { reject(new Error('Ses kaydı bulunamadı.')); return; }
      const audio = audioRef.current || new Audio(url);
      audioRef.current = audio;
      audio.src = url; audio.preload = 'auto'; audio.muted = false;
      setRoundAudioPlayback(audio, round, id);
      let timer, settled = false;
      const clean = () => { clearTimeout(timer); audio.onended = null; audio.onerror = null; cancelAudio = null; };
      const fail = (err) => { if (settled) return; settled = true; clean(); audio.pause(); reject(err); };
      cancelAudio = () => fail(new Error('cancelled'));
      audio.onended = () => { if (settled) return; settled = true; clean(); resolve(); };
      audio.onerror = () => fail(new Error('Ses oynatılamadı. Tekrar deneyelim.'));
      timer = setTimeout(() => fail(new Error('Ses yüklenemedi. Tekrar deneyelim.')), isCorrectFeedback(id) ? 1500 : 30000);
      try { Promise.resolve(audio.play()).catch(fail); } catch (err) { fail(err); }
    });
    const play = (id) => playOnce(id).catch((err) => {
      if (!alive) throw err;
      if (isCorrectFeedback(id)) return;
      return playOnce(id);
    });
    // A retry resumes at the failed clip, preserving all accepted answers.
    const speech = (requestedIds, references = false) => {
      const ids = requestedIds.filter(Boolean);
      let position = 0;
      return async () => {
        while (position < ids.length && alive) {
          const id = ids[position];
          if (references) setReferenceSound(round.basket_references?.find((ref) => ref.audio_id === id)?.sound || null);
          await play(id); position += 1;
        }
        if (alive && references) setReferenceSound(null);
      };
    };
    const prepareImage = () => Promise.all((baskets ? [round.items[cursor]]
      : [...(pictures ? round.choices : []), ...(round.reference ? [round.reference] : [])]).map((item) => new Promise((resolve, reject) => {
      const image = new Image();
      let timer;
      const clean = () => { clearTimeout(timer); image.onload = null; image.onerror = null; cancelImage = null; };
      const fail = () => { clean(); reject(new Error('Resim yüklenemedi. Tekrar deneyelim.')); };
      image.onload = () => { clean(); resolve(); };
      image.onerror = fail;
      cancelImage = fail;
      timer = setTimeout(fail, 30000);
      image.src = item.image.url;
    })));
    const ready = () => { if (alive) { locked = false; setPlaced(null); setDropOffset(null); setPhase('ready'); } };
    const run = async (operation) => {
      if (!alive || running) return;
      running = true; locked = true; setError(''); setPhase('playing');
      retry = () => run(operation);
      try { await operation(); }
      catch (err) {
        if (alive) { setError(err.name === 'NotAllowedError' ? 'Ses başlayamadı. Tekrar Dene düğmesine dokun.'
          : err.message); setPhase('retry'); }
      } finally { running = false; }
    };
    const startSpeech = speech([...(round.intro_sequence || [
      ...(round.basket_references || []).map((ref) => ref.audio_id), round.intro_audio_id]),
      ...(silent || round.intro_sequence?.includes(round.items[0].audio_id) ? [] : [round.items[0].audio_id])], true);
    let imagePrepared = false;
    const start = () => run(async () => {
      // Start the first play directly from the user's gesture; image preparation follows.
      await startSpeech();
      if (!imagePrepared) { await prepareImage(); imagePrepared = true; }
      if (!alive) return;
      report(); ready();
    });
    const replay = (instruction = false) => {
      if (locked) return;
      const sequence = speech(instruction ? [round.intro_audio_id, ...(silent ? [] : [round.items[cursor].audio_id])] : silent ? [] : [round.items[cursor].audio_id]);
      run(async () => { await sequence(); ready(); });
    };
    const help = () => {
      if (locked || helpShown) return;
      helpShown = true;
      if (!round.unmeasured_help) events = [...events, { kind: 'help_prompt' }];
      report();
      const sequence = speech([round.help_audio_id]);
      run(async () => { await sequence(); ready(); });
    };
    const arm = () => { if (!locked) { chosen = true; setArmed(true); } };
    const select = (sound, fromDrag = false, dragOrigin = null) => {
      if (locked || (baskets && !chosen && !fromDrag) || !round.choice_orders[cursor].includes(sound)) return;
      locked = true;
      const item = round.items[cursor];
      events = [...events, { kind: 'select', item_id: item.id, selected_id: sound }];
      const result = report();
      const correct = item.target_sound === sound;
      // Only a genuine basket answer changes feedback; listening and arming do not.
      if (isL4G05) setWrongFeedback((value) => correct ? 0 : value + 1);
      const repeated = events.filter((event) => event.kind === 'select' && event.item_id === item.id && event.selected_id === sound).length > 1;
      const sequence = speech(correct ? ['s_l1_g2_pop'] : silent ? [round.wrong_audio_id]
        : repeated ? [item.audio_id] : [round.wrong_audio_id, item.audio_id]);
      const finishSpeech = speech(round.skip_completion_speech ? [] : [round.complete_audio_id]);
      let advanced = false, prepared = false, animated = false;
      const nextSpeech = speech(!silent && cursor + 1 < round.items.length ? [round.items[cursor + 1].audio_id] : []);
      chosen = false; setArmed(false); setPlaced(correct ? sound : 'wrong');
      if (correct && baskets) {
        const origin = dragOrigin || source.current.getBoundingClientRect();
        const target = optionElements.current[sound].getBoundingClientRect();
        setDropOffset({ x: target.x + target.width / 2 - origin.x - origin.width / 2,
          y: target.y + target.height / 2 - origin.y - origin.height / 2 });
      }
      run(async () => {
        await sequence();
        if (!alive) return;
        if (!correct) { ready(); return; }
        if (!animated) {
          await new Promise((resolve) => {
            const timer = setTimeout(resolve, 350);
            cancelDelay = () => { clearTimeout(timer); resolve(); };
          });
          cancelDelay = null; animated = true;
        }
        if (!alive) return;
        if (result.round_completed) {
          setPhase('finishing'); await finishSpeech();
          if (alive) {
            if (round.next_round && onNextRound) onNextRound(round.next_round);
            else completion.current(result.summary);
          }
          return;
        }
        if (!advanced) { cursor += 1; advanced = true; setIndex(cursor); setPlaced(null); setDropOffset(null); }
        if (!prepared) { await prepareImage(); prepared = true; }
        await nextSpeech(); ready();
      });
    };
    handlers.current = { start, replay, help, arm, select,
      replayOption: (id) => {
        if (locked || silent) return;
        const clip = round.choices?.find((choice) => choice.id === id)?.audio_id;
        if (clip) { const sequence = speech([clip]); run(async () => { await sequence(); ready(); }); }
      },
      retry: () => retry?.(), cancelArm: () => { chosen = false; setArmed(false); } };
    if (round.continuing) start();
    return () => { alive = false; locked = true; cancelAudio?.(); cancelImage?.(); cancelDelay?.(); audioRef.current?.pause(); };
  }, [round, baskets, pictures, silent, audioRef, localProgress, onNextRound, isL4G05]);

  const item = round.items[index];
  const pointerDown = (event) => {
    if (phase !== 'ready') return;
    suppressClick.current = false;
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false,
      origin: event.currentTarget.getBoundingClientRect() };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const pointerMove = (event) => {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    const x = event.clientX - current.x, y = event.clientY - current.y;
    if (Math.hypot(x, y) > 8) current.moved = true;
    if (current.moved) setOffset({ x, y });
  };
  const pointerUp = (event) => {
    const current = drag.current;
    if (!current || current.id !== event.pointerId) return;
    drag.current = null; setOffset(null);
    if (current.moved) {
      suppressClick.current = true;
      const sound = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-sound-choice]')?.dataset.soundChoice;
      if (sound) handlers.current.select(sound, true, current.origin);
      else handlers.current.cancelArm();
    }
  };
  const choices = round.choice_orders[index];
  const status = <Typography role="status" aria-live={isL4G05 ? 'polite' : undefined} aria-atomic={isL4G05 ? 'true' : undefined} className="sound-hunt-status">
    {error || (isL4G05 && wrongFeedback > 0 && <span key={wrongFeedback} className="letter-hunt-wrong-capsule"><CloseRoundedIcon aria-hidden="true" /> Bir daha deneyelim.</span>)}
  </Typography>;
  return <Box className={`sound-hunt sound-choice ${isL4G05 ? 'l4-tour3 l4-g05 l4-hunt-feedback' : ''} ${baskets ? 'sound-baskets' : syllables ? 'syllable-choice' : pictureWord ? 'picture-word-choice' : 'heard-sound-choice'} ${armed || offset ? 'has-picked-picture' : ''}`}>
    <GardenBackdrop />
    <Box className="sound-hunt-content">
      <MiniTaskGameHeader classTitle={classTitle} soundEnabled soundRequired />
      <Box className="sound-hunt-header">
        <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRoundedIcon />}>Ana sayfa</Button>
        <Typography component="h1" className="sound-hunt-title">{withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi</Typography>
        <Box className="sound-hunt-progress" aria-label={round.progress_text ? `Meydan okuma ${round.progress_text}` : `Soru ${index + 1} / ${round.items.length}`}>
          <Typography sx={{ fontWeight: 900, fontSize: 'inherit' }}>{round.progress_text || `${index + 1} / ${round.items.length}`}</Typography>
        </Box>
      </Box>
      <Box className="sound-hunt-bottom">
        <Typography className="sound-hunt-badge">{round.badge_title || (baskets ? '🧺 Ses Sepetleri' : syllables ? '🎧 Heceleri Yakala' : '⚡ Hangisini Duydun?')}</Typography>
        {syllables && <Box className="syllable-choice-cue" aria-hidden="true"><VolumeUpRoundedIcon /> Dinle <EastRoundedIcon /><TouchAppRoundedIcon /> Kartı seç</Box>}
        {pictureWord && <Box className="syllable-choice-cue" aria-hidden="true">Resme bak <EastRoundedIcon /><TouchAppRoundedIcon /> Kelimeyi seç</Box>}
        {baskets && <Box className="sound-basket-steps" aria-hidden="true">
          <span><VolumeUpRoundedIcon /> Dinle</span><EastRoundedIcon className="step-arrow" />
          <span className={phase === 'ready' && !armed && !offset ? 'is-active' : ''}><TouchAppRoundedIcon /> Dokun veya taşı</span>
          <EastRoundedIcon className="step-arrow" /><span className={armed || offset ? 'is-active' : ''}><ShoppingBasketRoundedIcon /> Sepeti seç</span>
        </Box>}
        <Box className={baskets ? 'sound-basket-playfield' : undefined}>
        {baskets && <svg className="sound-basket-paths" viewBox="0 0 1000 300" preserveAspectRatio="none" aria-hidden="true">
          <path d="M385 125Q290 105 210 165M615 125Q710 105 790 165" />
          <path className="path-tip" d="M211 147L210 165L230 166M789 147L790 165L770 166" />
        </svg>}
        <Box className="sound-choice-source-area">
          {silent && round.written_word && <Box className="silent-written-word" aria-label="Yazılı kelime"><LetterSequence value={round.written_word} /></Box>}
          {round.reference && <img className="choice-reference-image" src={round.reference.image.url} alt="" />}
          {baskets && <button ref={source} type="button" aria-label="Resmi seç" aria-pressed={armed} disabled={phase !== 'ready'}
            className={`sound-choice-source ${armed ? 'is-armed' : ''} ${offset ? 'is-dragging' : ''} ${dropOffset ? 'is-placed' : ''} ${placed === 'wrong' ? 'is-returning' : ''}`}
            style={offset ? { transform: `translate(${offset.x}px, ${offset.y}px)` }
              : dropOffset ? { transform: `translate(${dropOffset.x}px, ${dropOffset.y}px) scale(0.35)` } : undefined}
            onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerUp}
            onPointerCancel={() => { drag.current = null; setOffset(null); handlers.current.cancelArm(); }}
            onClick={(event) => {
              // Ignore the pointer click generated by a drag; keyboard activation
              // (detail=0) remains available immediately after a cancelled drag.
              const suppressed = suppressClick.current && event.detail > 0;
              suppressClick.current = false;
              if (!suppressed) handlers.current.arm();
            }}>
            <img key={item.id} src={item.image.url} alt="" draggable={false} />
          </button>}
          {!silent && <MiniTaskReplayButton className={`sound-choice-replay ${baskets ? '' : 'is-large'}`} label={syllables ? 'Heceyi yeniden dinle' : 'Kelimeyi yeniden dinle'}
            disabled={phase !== 'ready'} onClick={() => handlers.current.replay()} />
          }
          {baskets && <span className={`sound-basket-action ${armed || offset ? 'is-picked' : ''}`} aria-hidden="true">
            <TouchAppRoundedIcon />{armed || offset ? 'Şimdi bir sepet seç' : 'Dokun veya taşı'}
          </span>}
        </Box>
        <Box className="sound-choice-options" data-choice-count={choices.length}>
          {choices.map((sound, choiceIndex) => {
            const option = <button ref={(element) => { optionElements.current[sound] = element; }} type="button" key={sound} data-sound-choice={sound}
            aria-label={pictures ? `${choiceIndex + 1}. resmi seç` : baskets ? `${sound} sepetini seç` : pictureWord ? `${sound} kelimesini seç` : syllables ? `${sound} hecesini seç` : `${sound} harfini seç`}
            className={`sound-choice-option ${baskets ? 'is-basket' : ''} ${placed === sound ? 'is-correct' : ''} ${referenceSound === sound ? 'is-reference' : ''}`}
            disabled={phase !== 'ready' || (baskets && !armed && !offset)} onClick={() => handlers.current.select(sound)}>
            {baskets && <SoundBasketArt />}
            <span className={baskets ? 'sound-basket-letter' : undefined}>{pictures
              ? <img className="choice-answer-image" src={round.choices.find((choice) => choice.id === sound).image.url} alt="" />
              : syllables || pictureWord ? <LetterSequence value={sound} /> : <Grade1Letter letter={sound} />}</span>
            {baskets && round.basket_references?.find((reference) => reference.sound === sound)?.image
              && <img className="basket-reference-image" src={round.basket_references.find((reference) => reference.sound === sound).image.url} alt="" />}
            {baskets && <span className="sound-basket-target" aria-hidden="true"><TouchAppRoundedIcon /></span>}
            {placed === sound && <span className="sound-choice-check" aria-hidden="true">✓</span>}
          </button>;
            return pictures ? <Box key={sound} className={`sound-picture-option ${silent ? '' : 'has-option-replay'}`}>
              {option}
              {!silent && <Button className="sound-picture-replay" aria-label={`${choiceIndex + 1}. resmin adını dinle`}
                disabled={phase !== 'ready'} onClick={() => handlers.current.replayOption(sound)}><VolumeUpRoundedIcon /></Button>}
            </Box> : option;
          })}
        </Box>
        </Box>
        {isL4G05 && status}
        <Box className="sound-hunt-controls">
          <Button className="sound-hunt-control" disabled={!['waiting', 'ready', 'retry'].includes(phase)}
            startIcon={<RecordVoiceOverRoundedIcon />} onClick={() => phase === 'waiting' ? handlers.current.start()
              : phase === 'retry' ? handlers.current.retry() : handlers.current.replay(true)}>
            {phase === 'retry' ? 'Tekrar Dene' : 'Yönergeyi dinleyelim'}
          </Button>
        </Box>
        {!isL4G05 && status}
      </Box>
    </Box>
  </Box>;
}
